import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FacettensucheService,
  SearchResult,
} from '../services/facettensuche.service';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'juspedia-search',
  standalone: true,
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  imports: [FormsModule, HttpClientModule, CommonModule],
  providers: [FacettensucheService, HttpClientModule],
})
export class WidgetComponent implements OnInit {
  query: string = '';
  searchText: string = '';
  filteredResults: SearchResult[] = []; // gefilterte Ergebnisse
  selectedQuelle: string[] = []; // ausgewählte quellen für filter
  selectedTypes: string[] = []; // ausgewählte types für filter
  selectedTags: string[] = []; // Gespeicherte Tags
  currentTag: string = ''; // Aktueller Eingabewert
  allTagwords = new Set<string>();
  allTagwordsCount: { [key: string]: number } = {};
  allTypeOptions = new Set<string>();
  allQuelleOptions = new Set<string>();
  documents: SearchResult[] = [];
  selectedNorms: string[] = [];
  currentNorm: string = '';
  allNormwords = new Set<string>();
  allNormwordsCount: { [key: string]: number } = {};
  isLoading: boolean = false;
  searchOrder: string = 'created';
  hasLoaded: boolean = false;

  // Pagination properties
  currentPage: number = 1;
  readonly itemsPerPage: number = 50;
  totalPages: number = 0;

  constructor(
    private FacettensucheService: FacettensucheService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    // is the Q parameter set in the URL?
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    if (query) {
      this.query = query;
      this.search();
    }
  }

  // SCHLAGWÖRTER

  addTag() {
    const tag = this.currentTag.trim();
    if (tag && this.allTagwords.has(tag) && !this.selectedTags.includes(tag)) {
      this.selectedTags.push(tag);
      this.allTagwords.delete(tag);
      this.currentTag = ''; // Eingabefeld leeren nachdem man eins eingegeben hat
      this.applyFilters();
    }
  }

  // NORM

  addNorm() {
    const norm = this.currentNorm.trim();
    if (
      norm &&
      this.allNormwords.has(norm) &&
      !this.selectedNorms.includes(norm)
    ) {
      this.selectedNorms.push(norm);
      this.allNormwords.delete(norm);
      this.currentNorm = ''; // Eingabefeld leeren
      this.applyFilters();
    }
  }

  //SUCHE

  search() {
    this.isLoading = true; // Start Ladeanzeige
    // alle Ergebniss entfernen - wir suchen neu
    this.currentPage = 1;
    this.totalPages = 0;
    this.allNormwords.clear();
    this.allTagwords.clear();
    this.documents = [];
    this.filteredResults = [];
    this.FacettensucheService.suggest(this.query).subscribe({
      // liefert einen erweiterten Suchstring
      next: (newQuery) => {
        this.searchText = newQuery;
        this.FacettensucheService.search(this.searchText).subscribe({
          next: (items) => {
            this.documents = items;
            // suche nach den vorgekommenen Normen und Tags
            this.documents.forEach((item) => {
              item.keywords?.forEach((keyword) => {
                this.allTagwords.add(keyword);
                this.allTagwordsCount[keyword] =
                  (this.allTagwordsCount[keyword] || 0) + 1;
              });
              item.norm?.forEach((norm) => {
                this.allNormwords.add(norm);
                this.allNormwordsCount[norm] =
                  (this.allNormwordsCount[norm] || 0) + 1;
              });
            });
            // Filter anwenden
            this.applyFilters();
            // Sortieren
            this.sortResults();
          },
          error: (err) => {
            console.error('Fehler bei der Suche:', err);
          },
          complete: () => {
            this.isLoading = false; // Ladeanzeige beenden
            this.hasLoaded = true; // wir haben gesucht
          },
        });
      },
      error: (err) => {
        console.error('Fehler bei den Wortvorschlägen', err);
      },
    });
  }

  // liefere die gefundenen Dokumenttypen
  foundTypes(base: SearchResult[] = this.filteredResults): Set<string> {
    if (base.length === 0) {
      return new Set<string>(this.allDocumentTypes);
    }
    const result = new Set<string>();
    base.forEach((item) => result.add(item.type));
    return result;
  }

  // liefere die gefundenen Quellen
  foundQuellen(base: SearchResult[] = this.filteredResults): Set<string> {
    if (base.length === 0) {
      return new Set<string>(this.allQuellen);
    }
    const result = new Set<string>();
    base.forEach((item) => result.add(item.quelle));
    return result;
  }

  filterByQuelle(base: SearchResult[] = this.filteredResults): WidgetComponent {
    this.filteredResults =
      this.selectedQuelle.length === 0
        ? base
        : base.filter((item) => this.selectedQuelle.includes(item.quelle));
    return this;
  }

  filterByType(base: SearchResult[] = this.filteredResults): WidgetComponent {
    this.filteredResults =
      this.selectedTypes.length === 0
        ? base
        : base.filter((item) => this.selectedTypes.includes(item.type));
    return this;
  }

  filterByTag(base: SearchResult[] = this.filteredResults): WidgetComponent {
    this.filteredResults =
      this.selectedTags.length === 0
        ? base
        : base.filter((item) =>
            this.selectedTags.some((tag) => item.keywords.includes(tag)),
          );
    return this;
  }

  filterByNorm(base: SearchResult[] = this.filteredResults): WidgetComponent {
    this.filteredResults =
      this.selectedNorms.length === 0
        ? base
        : base.filter((item) =>
            this.selectedNorms.some((norm) => item.norm.includes(norm)),
          );
    return this;
  }

  // filterlogik
  applyFilters() {
    // erlaubte quellen...
    this.allQuelleOptions = this.filterByType(this.documents)
      .filterByNorm()
      .filterByTag()
      .foundQuellen();
    // erlaubte Typen...
    this.allTypeOptions = this.filterByQuelle(this.documents)
      .filterByNorm()
      .filterByTag()
      .foundTypes();
    // Filtern durchführen
    this.filterByQuelle(this.documents)
      .filterByType()
      .filterByTag()
      .filterByNorm();
    // Anzahl der Pages bestimmen
    this.totalPages = Math.ceil(
      this.filteredResults.length / this.itemsPerPage,
    );
  }

  // filter aktualisieren
  toggleQuelle(quelle: string) {
    if (this.selectedQuelle.includes(quelle)) {
      this.selectedQuelle = this.selectedQuelle.filter((f) => f !== quelle);
    } else {
      this.selectedQuelle.push(quelle);
    }
    this.applyFilters();
  }

  toggleType(type: string) {
    if (this.selectedTypes.includes(type)) {
      this.selectedTypes = this.selectedTypes.filter((t) => t !== type);
    } else {
      this.selectedTypes.push(type);
    }
    this.applyFilters();
  }

  removeNorm(norm: string) {
    if (norm && this.selectedNorms.includes(norm)) {
      this.selectedNorms = this.selectedNorms.filter((t) => t !== norm);
      this.allNormwords.add(norm);
      this.applyFilters();
    }
  }
  removeTag(tag: string) {
    if (tag && this.selectedTags.includes(tag)) {
      this.selectedTags = this.selectedTags.filter((t) => t !== tag);
      this.allTagwords.add(tag);
      this.applyFilters();
    }
  }
  // Date ins Europäische konvertieren
  formatDate(datestring: string): string {
    if (!datestring) return '';

    const date = new Date(datestring);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // Entferne HTML Tags aus Zusammenfassung
  stripHtml(html: string): string {
    if (!html) return '';
    // Sicherstellen, dass Leerzeichen vorhanden sein werden
    html = html.replaceAll('<', ' <').replaceAll('>', '> ');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  // Beschränke Text in Zusammenfassung auf 30 Wörter
  truncateText(text: string, wordLimit: number = 30): string {
    if (!text) return '';

    text = this.stripHtml(text);

    const lowerText = text.toLowerCase();
    const lowerQuery = this.searchText.toLowerCase();

    const words = text.split(/\s+/);
    const lowerWords = lowerText.split(/\s+/);

    // Finde erstes Vorkommen des Suchbegriffs
    const matchIndex = lowerWords.findIndex((word) =>
      word.includes(lowerQuery),
    );

    // Wenn nichts gefunden wird, einfach die ersten 30 Wörter wie bisher
    if (matchIndex === -1) {
      return words.slice(0, wordLimit).join(' ') + ' ...';
    }

    const halfLimit = Math.floor(wordLimit / 2);
    const start = Math.max(0, matchIndex - halfLimit);
    const end = Math.min(words.length, matchIndex + halfLimit);

    return (
      (start > 0 ? '... ' : '') +
      words.slice(start, end).join(' ') +
      (end < words.length ? ' ...' : '')
    );
  }

  // Text hervorheben

  highlightText(text: string): string {
    if (!this.searchText) return text;

    const words = this.searchText
      .split(/\s+/)
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .filter((w) => w.length > 0);

    if (words.length === 0) return text;

    const regex = new RegExp(`(${words.join('|')})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  // Ergebnisse auf Seiten aufteilen
  get paginatedResults(): SearchResult[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredResults.slice(startIndex, endIndex);
  }

  // Seitenzahlen für Template
  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  // Seitenwechsel
  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  // Vergleichsfunktion für Strings
  cmpString(
    field: keyof SearchResult,
  ): (x: SearchResult, y: SearchResult) => number {
    return (x, y) => {
      return -(x[field] as string).localeCompare(y[field] as string);
    };
  }

  // Vergleichsfunktion für numerisch
  cmpNumber(
    field: keyof SearchResult,
  ): (x: SearchResult, y: SearchResult) => number {
    return (x, y) => {
      return -((x[field] as number) - (y[field] as number));
    };
  }

  sortResults() {
    let sortFn: (x: SearchResult, y: SearchResult) => number;
    if (this.searchOrder === 'score') {
      sortFn = this.cmpNumber('score');
      console.log('Sort by score');
    } else if (this.searchOrder === 'date') {
      sortFn = this.cmpString('docDate');
      console.log('Sort by date');
    } else {
      sortFn = this.cmpString('dateCreated');
      console.log(this.searchOrder, 'Sort by dateCreated');
    }
    this.filteredResults.sort(sortFn);
    console.log(JSON.stringify(this.filteredResults));
  }

  // Tags sortiert wiedergeben
  get sortedTags(): string[] {
    return Array.from(this.allTagwords).sort(
      (a, b) =>
        (this.allTagwordsCount[b] || 0) - (this.allTagwordsCount[a] || 0),
    );
  }

  // Dokumenttypen für Widget zugänglich machen
  readonly allDocumentTypes = [
    'Judikatur',
    'Rechtsauskunft',
    'Erlass',
    'Material zur Auslegung von Rechtstexten',
    'Merkblatt oder FAQ',
    'angehängte Datei',
    'Sonstige Information'    
  ];

  // Quellen für Widget zugänglich machen
  readonly allQuellen = [
    'Landesverwaltungsgericht',
    'Verwaltungsgerichtshof (VwGH)',
    'Verfassungsgerichtshof (VfGH)',
    'EuGH',
    'sonstige Gerichte',
    'BMLUK',
    'andere Ministerien',
    'Europäische Kommission',
    'sonstige Quelle',
  ];

  // "Math" für Template verfügbar machen
  readonly Math = Math;
}
